import { Injectable, inject } from '@angular/core';
import {
  Firestore,
  collection,
  collectionData,
  doc,
  getDoc,
  addDoc,
  updateDoc,
  serverTimestamp,
} from '@angular/fire/firestore';
import {
  Storage,
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from '@angular/fire/storage';
import { Observable } from 'rxjs';
import { Espacio } from '../models/espacio.model';

@Injectable({ providedIn: 'root' })
export class EspacioService {
  private firestore = inject(Firestore);
  private storage   = inject(Storage);

  private col(residencialId: string) {
    return collection(this.firestore, `residenciales/${residencialId}/espacios`);
  }

  private ref(residencialId: string, espacioId: string) {
    return doc(this.firestore, `residenciales/${residencialId}/espacios/${espacioId}`);
  }

  /** Devuelve TODOS los espacios (activos e inactivos). El filtrado lo hace el componente. */
  getAll(residencialId: string): Observable<Espacio[]> {
    return collectionData(this.col(residencialId), { idField: 'id' }) as Observable<Espacio[]>;
  }

  async getById(residencialId: string, espacioId: string): Promise<Espacio | null> {
    const snap = await getDoc(this.ref(residencialId, espacioId));
    if (!snap.exists()) return null;
    return { id: snap.id, ...snap.data() } as Espacio;
  }

  async crear(residencialId: string, datos: Omit<Espacio, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    const docRef = await addDoc(this.col(residencialId), {
      ...datos,
      activo:    true,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    return docRef.id;
  }

  async actualizar(
    residencialId: string,
    espacioId: string,
    cambios: Partial<Omit<Espacio, 'id' | 'createdAt'>>
  ): Promise<void> {
    await updateDoc(this.ref(residencialId, espacioId), {
      ...cambios,
      updatedAt: serverTimestamp(),
    });
  }

  async desactivar(residencialId: string, espacioId: string): Promise<void> {
    await updateDoc(this.ref(residencialId, espacioId), {
      activo:    false,
      updatedAt: serverTimestamp(),
    });
  }

  async uploadFoto(residencialId: string, espacioId: string, file: File): Promise<string> {
    const ext     = file.name.split('.').pop() ?? 'jpg';
    const fotoRef = ref(this.storage, `residenciales/${residencialId}/espacios/${espacioId}/foto.${ext}`);
    await uploadBytes(fotoRef, file);
    return getDownloadURL(fotoRef);
  }

  async deleteFoto(url: string): Promise<void> {
    try {
      await deleteObject(ref(this.storage, url));
    } catch { /* ignorar si ya fue eliminado */ }
  }
}
